import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type", "id", "ownerId"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import invariant from 'react-utils/invariant';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Box } from 'foundations-components/transitional/components/Box';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { ViewSharingEditor } from '../../manageSharing/components/ViewSharingEditor';
import { useFetchSingleView } from '../../singleView/hooks/useFetchSingleView';
import { useCreateViewModalState } from '../hooks/useCreateViewModalState';
import { getLangStringsForStrategy } from '../utils/getLangStringsForStrategy';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
//aka - clone a view with draft changes

export const CREATE_VIEW_MODAL_STRATEGIES = {
  CREATE_NEW: 'CREATE_NEW',
  CLONE_EXISTING: 'CLONE_EXISTING',
  SAVE_AS_NEW: 'SAVE_AS_NEW'
};
export const CreateViewFromViewObjectModal = ({
  strategy,
  viewObject,
  onFailure,
  onSuccess,
  onClose
}) => {
  invariant(viewObject.id === undefined, 'You cannot declare an id on a view you are trying to clone - if you are creating a view, omit id');
  invariant(viewObject.intId === undefined, 'You cannot declare an intId on a view you are trying to clone - if you are creating a view, omit intId');
  invariant(viewObject.idLabel === undefined, 'You cannot declare an idLabel on a view you are trying to clone - if you are creating a view, omit idLabel');
  invariant(viewObject.type !== 'DEFAULT', 'viewObject.type was "DEFAULT" which is invalid - if you are creating a view, omit type');
  invariant(viewObject.objectTypeId !== undefined, 'viewObject.objectTypeId was not defined');
  invariant(viewObject.namespaceName !== undefined, 'viewObject.namespaceName was not defined');
  invariant(viewObject.filterGroups, 'viewObject.filterGroups was not defined');
  invariant(viewObject.quickFilters, 'viewObject.quickFilters was not defined');
  const {
    isLoading,
    isError,
    textInputOnChange,
    handleSubmit,
    errorAffordance,
    errorValidationMessage,
    textInputValue,
    viewSharingUpdates,
    setViewSharingUpdates
  } = useCreateViewModalState({
    viewObject,
    createStrategy: strategy,
    onFailure,
    onSuccess
  });
  const {
    modalTitle,
    cancelButton,
    confirmButton
  } = getLangStringsForStrategy({
    strategy
  });
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "create-view-modal",
    "data-test-loading": isLoading,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: modalTitle
        })
      })]
    }), /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(DialogBody, {
        children: [!isLoading && /*#__PURE__*/_jsx("div", {
          "data-test-id": "create-view-modal-loaded"
        }), isLoading && /*#__PURE__*/_jsx("div", {
          "data-test-id": "create-view-modal-loading"
        }), isError && /*#__PURE__*/_jsx("div", {
          "data-test-id": "create-view-modal-error"
        }), errorAffordance, /*#__PURE__*/_jsx(FormControl, {
          "data-test-id": "view-name-input-form-control",
          error: Boolean(isError && !isLoading),
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.createViewModal.nameInput.label"
          }),
          required: true,
          validationMessage: !isLoading && errorValidationMessage,
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(Box, {
              shrink: 1,
              grow: 1,
              children: /*#__PURE__*/_jsx(TextInput, {
                "data-test-id": "create-single-view-modal-input",
                onChange: textInputOnChange,
                value: textInputValue,
                autoFocus: true
              })
            }), /*#__PURE__*/_jsx(Box, {
              shrink: 0,
              grow: 0,
              children: /*#__PURE__*/_jsx(LoadingSpinner, {
                failed: Boolean(isError),
                showResult: !isLoading
              })
            })]
          })
        }), /*#__PURE__*/_jsx(ViewSharingEditor, {
          originalView: viewObject,
          viewUpdates: viewSharingUpdates,
          setViewUpdates: setViewSharingUpdates
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          disabled: Boolean(isError || isLoading),
          onClick: handleSubmit,
          "data-test-id": "create-single-view-modal-submit-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: confirmButton
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: cancelButton
          })
        })]
      })]
    })]
  });
};
export const CreateViewModal = ({
  strategy,
  objectTypeId,
  namespaceName,
  viewId,
  onSuccess,
  onFailure,
  onClose
}) => {
  const {
    data
  } = useFetchSingleView({
    objectTypeId,
    namespaceName,
    id: viewId
  });
  if (!data) {
    return null;
  }
  const _data$singleViewById = data.singleViewById,
    viewObject = _objectWithoutPropertiesLoose(_data$singleViewById, _excluded);
  return /*#__PURE__*/_jsx(CreateViewFromViewObjectModal, {
    viewObject: viewObject,
    strategy: strategy,
    onSuccess: onSuccess,
    onFailure: onFailure,
    onClose: onClose
  });
};